import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AlertCircle, ArrowRight, Landmark, Loader2, Newspaper } from "lucide-react";
import Seo from "../components/Seo";
import NewsCard from "../components/NewsCard";
import { GoldButton } from "../components/Buttons";
import { Reveal } from "../components/Reveal";
import { IRS_NEWS } from "../data/content";

const API = process.env.REACT_APP_BACKEND_URL;

const StateBox = ({ icon: Icon, text, testid }) => (
    <div className="border border-[#E7E4DC] bg-white rounded-xl p-12 text-center" data-testid={testid}>
        <Icon className="h-8 w-8 text-[#C8A45D] mx-auto" />
        <p className="text-[#3D434D] mt-4 max-w-md mx-auto leading-relaxed">{text}</p>
    </div>
);

const fmtSynced = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return "";
    }
};

export default function IrsNews() {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let alive = true;
        axios
            .get(`${API}/api/news`, { timeout: 15000 })
            .then(({ data }) => {
                if (alive) setData(data);
            })
            .catch(() => {
                if (alive) setFailed(true);
            });
        return () => {
            alive = false;
        };
    }, []);

    const articles = data?.articles || [];
    const state = failed
        ? "error"
        : !data
            ? "loading"
            : !data.enabled
                ? "disabled"
                : articles.length === 0
                    ? "empty"
                    : "ready";

    return (
        <div data-testid="page-irs-news">
            <Seo
                title="IRS News & Tax Updates | Freedom Tax Filer"
                description="The latest IRS news, filing deadlines, tax tips, refund updates, and scam alerts — synced automatically from the official IRS.gov newsroom."
                path="/irs-news"
            />

            <section className="pt-32 pb-20 bg-[#172337] border-b border-white/10" data-testid="irs-news-hero">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">{IRS_NEWS.eyebrow}</p>
                        <div className="gold-rule mt-5" />
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-6 leading-[1.06] tracking-tight max-w-3xl">
                            IRS News &amp; <em className="italic text-gold-gradient">Tax Updates.</em>
                        </h1>
                        <p className="text-white/65 text-base sm:text-lg leading-relaxed mt-6 max-w-2xl">{IRS_NEWS.heroText}</p>
                        <div className="inline-flex items-center gap-2 mt-8 border border-[#C8A45D]/40 bg-[#C8A45D]/10 rounded-full px-4 py-2 text-[#E4D2A3] text-[11px] uppercase tracking-[0.18em]" data-testid="news-live-badge">
                            <Landmark className="h-3.5 w-3.5" /> {IRS_NEWS.liveBadge}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad bg-[#FAFAF7]" data-testid="irs-news-section">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    {state === "loading" && (
                        <div className="flex items-center justify-center gap-3 text-[#737985] py-16" data-testid="news-loading">
                            <Loader2 className="h-5 w-5 animate-spin" /> Loading the latest IRS updates…
                        </div>
                    )}
                    {state === "error" && <StateBox icon={AlertCircle} text={IRS_NEWS.errorText} testid="news-state-error" />}
                    {state === "disabled" && <StateBox icon={Newspaper} text={IRS_NEWS.disabledText} testid="news-state-disabled" />}
                    {state === "empty" && <StateBox icon={Newspaper} text={IRS_NEWS.emptyText} testid="news-state-empty" />}
                    {state === "ready" && (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {articles.map((a, i) => (
                                    <Reveal key={a.id} delay={Math.min(i, 8) * 0.06} className="h-full">
                                        <NewsCard article={a} index={i} />
                                    </Reveal>
                                ))}
                            </div>
                            {data.last_synced ? (
                                <p className="text-[#9aa1ab] text-xs mt-10" data-testid="news-synced-note">
                                    {IRS_NEWS.syncedNote} {fmtSynced(data.last_synced)}. Links open the official IRS article
                                    in a new tab.
                                </p>
                            ) : null}
                        </>
                    )}

                    <Reveal delay={0.1}>
                        <div
                            className="mt-16 border border-[#C8A45D]/40 bg-[#172337] rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row gap-6"
                            data-testid="news-disclaimer"
                        >
                            <Landmark className="h-6 w-6 text-[#C8A45D] shrink-0 mt-1" />
                            <p className="text-white/60 text-[13px] leading-relaxed">{IRS_NEWS.disclaimer}</p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div
                            className="mt-12 relative border border-[#C8A45D]/40 bg-[#111318] rounded-2xl px-8 sm:px-14 py-14 overflow-hidden shadow-xl"
                            data-testid="news-cta"
                        >
                            <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-transparent via-[#C8A45D] to-transparent" />
                            <h3 className="font-display text-3xl sm:text-4xl text-white leading-tight max-w-xl">{IRS_NEWS.ctaTitle}</h3>
                            <p className="text-white/60 leading-relaxed mt-4 max-w-xl">{IRS_NEWS.ctaText}</p>
                            <div className="mt-9">
                                <GoldButton asChild data-testid="news-cta-button">
                                    <Link to="/contact">
                                        {IRS_NEWS.ctaButton} <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </GoldButton>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
